import { 
  TrendingUp, TrendingDown, DollarSign, Users, BarChart3, CreditCard,
  ShoppingCart, Package, Activity, Zap, Target, Award, Calendar, Clock,
  FileText, Mail, Phone, MapPin, Home, Settings, type LucideIcon
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { formatNumber } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { cn } from '@/lib/utils';

// Mapeamento de emojis para ícones Lucide (para retrocompatibilidade)
const ICON_MAP: Record<string, LucideIcon> = {
  '💰': DollarSign,
  '👥': Users,
  '📊': BarChart3,
  '💳': CreditCard,
  '🛒': ShoppingCart,
  '📦': Package,
  '💪': Activity,
  '⚡': Zap,
  '🎯': Target,
  '🏆': Award,
  '📅': Calendar,
  '⏰': Clock,
  '📄': FileText,
  '📧': Mail,
  '📞': Phone,
  '📍': MapPin,
  '🏠': Home,
  '⚙️': Settings,
};

interface MetricCardProps {
  title: string;
  value: number;
  icon?: string;
  format?: 'number' | 'currency' | 'percentage';
  trend?: {
    value: number;
    isPositive: boolean;
  };
  style?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

export default function MetricCard({
  title,
  value,
  icon,
  format = 'number',
  trend,
  style,
}: MetricCardProps) {
  // Determinar qual ícone usar
  // 1. Se for um nome de ícone Lucide (ex: "DollarSign")
  // 2. Se for um emoji mapeado (ex: "💰")
  // 3. Fallback para null
  let IconComponent: LucideIcon | null = null;
  
  if (icon) {
    // Tentar como nome de ícone Lucide primeiro
    const LucideIcon = Icons[icon as keyof typeof Icons] as any;
    if (LucideIcon) {
      IconComponent = LucideIcon;
    } else {
      // Tentar como emoji mapeado
      IconComponent = ICON_MAP[icon] || null;
    }
  }
  
  return (
    <Card
      className="h-full hover:shadow-md transition-shadow"
      style={{
        backgroundColor: style?.backgroundColor,
        color: style?.textColor,
      }}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{formatNumber(value, format)}</p>
            
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                {trend.isPositive ? (
                  <TrendingUp className="w-4 h-4 text-green-500 dark:text-green-400" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 dark:text-red-400" />
                )}
                <span
                  className={cn(
                    "text-sm font-medium",
                    trend.isPositive ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                  )}
                >
                  {Math.abs(trend.value)}%
                </span>
              </div>
            )}
          </div>
          
          {IconComponent && (
            <div className="p-3 bg-primary/10 rounded-lg">
              <IconComponent className="w-8 h-8 text-primary" strokeWidth={2} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
